import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ConsolidatedLocationView } from '@mark43/rms-api';
import { EventPanelFormType } from '@mark43/cad-api';
import { cloneDeep } from 'lodash';
import { useBootstrapState } from '../../../bootstrap/bootstrap-provider';
import { globalAttributes } from '../../../common/global-attributes';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { FormSelect } from '../../../forms/integrated-form-controls/form-select';
import { useIntegratedForm } from '../../../forms/use-integrated-form';
import {
    phoneCallDtoToPhoneCallOption,
    sortPhoneCallDtosByCreatedDate,
} from '../../../phone-calls/phone-call-dto-to-phone-call-option';
import { usePhoneCallsStore } from '../../../phone-calls/use-phone-calls';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { EventPanelTab } from '../components/event-panel-tab';
import { TabBody } from '../../../panels/entity-panels/tabs/tab-body';
import { AniAliSection } from './ani-ali-section';
import { getAmlCallForEvent, RapidSosSection } from './rapidsos-section';
import { logDebug } from '../../../monitoring/log';
import type { AmlCallView } from '../../../phone-calls/types';
import { useViewCallOnMap } from './use-view-call-on-map';

const WatchCallSelection = observer(function WatchCallSelection() {
    const { amlUser } = useEnabledFeatures();
    const viewCallOnMap = useViewCallOnMap();
    const phoneCalls = usePhoneCallsStore().activePhoneCallsList;
    const amlCalls = usePhoneCallsStore().amlCallsList;

    const form = useIntegratedForm<EventPanelFormType>().form;
    const eventPanelOptions = useBootstrapState().eventPanelOptions;
    const eventExists = !!useEventMetadataStore()?.state.metadata?.eventId;

    useEffect(() => {
        const subscription = form.watch((data, { name, type }) => {
            if (name === 'phoneCall' && type === 'change' && eventPanelOptions && !eventExists) {
                logDebug('[MAP_LINE_DEBUG] ANI/ALI selection in New Event', {
                    hasPhoneCallSelected: !!data?.phoneCall,
                    hasLocation: !!data.location,
                    eventExists,
                });
                const e911OriginOption = eventPanelOptions.eventOriginOptions.find((option) => {
                    return (
                        option.parentAttributeId === globalAttributes.eventOriginGlobal.NINE_ONE_ONE
                    );
                });
                if (e911OriginOption) {
                    form.setValue('eventOrigin', e911OriginOption);
                }
                const location = data.location
                    ? (cloneDeep(data.location) as ConsolidatedLocationView)
                    : undefined;

                const relatedPhoneCall = phoneCalls.find(
                    (phoneCall) => phoneCall.phoneNumberId === data?.phoneCall?.phoneNumberId
                );
                const relatedAmlCall = getAmlCallForEvent(amlCalls, relatedPhoneCall);

                if (relatedPhoneCall) {
                    logDebug('[MAP_LINE_DEBUG] Calling viewCallOnMap for phone call', {
                        hasLocation: !!location,
                    });
                    viewCallOnMap({
                        phoneCallView: relatedPhoneCall,
                        shouldClearAmlCall: !relatedAmlCall,
                        location,
                        dontFocusOrOpenMap: true,
                    });
                }
                if (amlUser && relatedAmlCall) {
                    logDebug('[MAP_LINE_DEBUG] Calling viewCallOnMap for AML call', {
                        hasLocation: !!location,
                    });
                    viewCallOnMap({
                        amlCallView: relatedAmlCall as AmlCallView,
                        shouldClearPhoneCall: !relatedPhoneCall,
                        location,
                        dontFocusOrOpenMap: true,
                    });
                }
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [form, eventPanelOptions, eventExists, phoneCalls, amlCalls, amlUser, viewCallOnMap]);
    return null;
});

export const AniAliRapidSosTab = observer(function AniAliRapidSosTab() {
    const enabledFeatures = useEnabledFeatures();
    const eventForm = useFormContext<EventPanelFormType>();
    const eventMetadata = useEventMetadataStore().state.metadata;
    const phoneCalls = usePhoneCallsStore().activePhoneCallsList;
    const sortedPhoneCalls = sortPhoneCallDtosByCreatedDate(phoneCalls);
    const phoneCallOptions = sortedPhoneCalls.map(phoneCallDtoToPhoneCallOption);
    return (
        <EventPanelTab title="ANI / ALI">
            <TabBody>
                {eventMetadata && !eventMetadata.eventPhoneCall ? (
                    <FormSelect
                        label="Call"
                        control={eventForm.control}
                        options={phoneCallOptions}
                        name="phoneCall"
                        asEditable={false}
                        menuPlacement="bottom"
                    />
                ) : undefined}
                <WatchCallSelection />
                <AniAliSection />
                {enabledFeatures.amlUser ? <RapidSosSection /> : undefined}
            </TabBody>
        </EventPanelTab>
    );
});
